import { gsap } from 'gsap';
import { MotionPathPlugin } from 'gsap/MotionPathPlugin';

export default class OrbitingImage {
  public isMobileScreen?: boolean;

  public tl?: gsap.core.Timeline;

  public radius: number;

  public static radiusInitial = 140;

  public imagesElements: HTMLElement[];

  public isPositionRight: boolean;

  public static rightScales = [
    {
      initial: 1,
      final: 0.5,
    },
    {
      initial: 0.5,
      final: 1,
    },
    {
      initial: 0.7,
      final: 0.9,
    },
  ];

  public static leftScales = [
    {
      initial: 0.5,
      final: 1,
    },
    {
      initial: 0.7,
      final: 0.9,
    },
    {
      initial: 1,
      final: 0.5,
    },
  ];

  private rootElement: HTMLElement;

  private static imagesWrapper: number;

  constructor(rootElement: HTMLElement) {
    OrbitingImage.imagesWrapper =
      rootElement.querySelector('.images-section')?.getBoundingClientRect().width || 0;
    this.isMobileScreen = window.innerWidth < 992;
    this.rootElement = rootElement;
    this.isPositionRight =
      this.rootElement
        .closest('[orbiting-images-position]')
        ?.getAttribute('orbiting-images-position') === 'right';
    this.radius = 140;
    this.imagesElements = [];
    window.addEventListener('resize', () => this.onResize());
  }

  public onResize() {
    OrbitingImage.imagesWrapper =
      this.rootElement.closest('.images-section')?.getBoundingClientRect().width || 0;
    this.isMobileScreen = window.innerWidth < 992;
    this.tl?.clear();
    this.run();
  }

  public async run() {
    if (window.innerWidth > 1200) {
      this.radius = OrbitingImage.radiusInitial * 1;
    } else if (window.innerWidth > 766) {
      this.radius = OrbitingImage.radiusInitial * 0.9;
    } else {
      this.radius = OrbitingImage.radiusInitial * 0.65;
    }

    this.imagesElements = Array.from(
      this.rootElement.querySelectorAll('.image-animate')
    ) as HTMLElement[];
    gsap.registerPlugin(MotionPathPlugin);
    this.tl = gsap.timeline({
      scrollTrigger: {
        trigger: this.rootElement,
        start: 'top 50%',
        end: `+=${this.rootElement.getBoundingClientRect().height}`,
        scrub: 1,
      },
    });
    this.imagesElements.forEach((image, i) => {
      this.addAnimation(image, i);
      this.setInitialPosition(image, i);
    });
  }

  public addAnimation(image: HTMLElement, i: number) {
    if (!this.tl) {
      throw new Error('timeLine is not defined!');
    }
    const scale = this.getScale(i);
    if (!scale) {
      throw new Error('scale is not defined!');
    }
    this.tl.to(
      image,
      {
        duration: 4,
        zIndex: 3,
        motionPath: {
          path: this.getCircularPath(i),
        },
      },
      0
    );
    this.tl.fromTo(
      image,
      {
        scale: scale.initial,
        duration: 4,
      },
      {
        scale: scale.final,
        duration: 4,
      },
      0
    );
  }

  public setInitialPosition(image: HTMLElement, index: number) {
    if (!this.radius) {
      throw new Error('radius is not set');
    }
    const imagesWidth = window.innerWidth > 768 ? 400 : 340;
    const leftOffset = this.isMobileScreen ? (OrbitingImage.imagesWrapper - imagesWidth) * 0.5 : 0;
    // const leftOffset = this.isMobileScreen ? 100 : 0;
    const r = this.radius;
    switch (index) {
      case 0:
        image.style.top = '50px';
        image.style.left = `${leftOffset}px`;
        return;
      case 1:
        image.style.top = '50px';
        image.style.left = `${leftOffset + r * Math.sqrt(3)}px`;
        return;
      case 2:
        image.style.top = `${1.5 * r + 50}px`;
        image.style.left = `${leftOffset + (r * Math.sqrt(3)) / 2}px`;
        return;
      default:
        throw new Error('invalid index');
    }
  }

  public getCircularPath(arcNumber: number): SVGPathElement {
    if (!this.radius) {
      throw new Error('radius is not set');
    }
    const r = this.radius;
    const circlePath = document.querySelector(
      `svg[circle-radius="${r}"] path[arc-number="${arcNumber}"]`
    );
    if (circlePath) {
      return circlePath as SVGPathElement;
    }
    const element = document.createElement('div');
    element.innerHTML = `
        <svg
        xmlns="http://www.w3.org/2000/svg"
        width="100"
        height="100"
        circle-radius="${r}"
        style="visibility: visible;position: fixed;"
        version="1.1">
            <!-- First arc -->
            <path arc-number="0" d="M 0 0 A ${r} ${r} 0 0 1 ${r * Math.sqrt(3)}  ${0}"  fill="none" stroke="red" stroke-width="2"/>
            <!-- Second arc -->
            <path arc-number="1" d="M 0 0 A ${r} ${r} 0 0 1 ${(-r * Math.sqrt(3)) / 2} ${1.5 * r}" fill="none" stroke="green" stroke-width="2"/>
            <!-- Third arc -->
            <path arc-number="2" d="M 0 0 A ${r} ${r} 0 0 1 ${(-r * Math.sqrt(3)) / 2} ${-1.5 * r}" fill="none" stroke="blue" stroke-width="2"/>
        </svg>
    `;
    document.body.append(element);
    return element.querySelector(`path[arc-number="${arcNumber}"]`) as SVGPathElement;
  }

  public getScale(index: number) {
    if (this.isPositionRight) {
      return OrbitingImage.rightScales[index];
    }
    return OrbitingImage.leftScales[index];
  }
}
